import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api';
import AuthContext from '../context/AuthContext';
import Offenses from '../components/Offenses';
import CourtHistory from '../components/CourtHistory';
import ProbationDetails from '../components/ProbationDetails';
import CommunityService from '../components/CommunityService';
import CaseNotes from '../components/CaseNotes';
import FamilyMembers from '../components/FamilyMembers';
import DistinctiveMarks from '../components/DistinctiveMarks';
import Mentorship from '../components/Mentorship';
import MedicalInfo from '../components/MedicalInfo';
import { formatDate } from '../utils/formatDate';
import clsx from 'clsx';

function JuvenileDetailPage() {
  const [juvenile, setJuvenile] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const { id } = useParams();
  const { hasPermission } = useContext(AuthContext);

  const fetchJuvenile = useCallback(async () => {
    try {
      const response = await apiClient.get(`/api/juveniles/${id}`);
      setJuvenile(response.data);
    } catch (err) {
      setError('Failed to fetch juvenile details.');
      console.error(err);
    }
  }, [id]);

  useEffect(() => {
    fetchJuvenile();
  }, [fetchJuvenile]);

  if (error) return <div className="card"><p className="text-red-500">{error}</p></div>;
  if (!juvenile) return <div className="card"><p>Loading...</p></div>;

  const TABS = [
    { key: 'profile', label: 'Profile', permission: true },
    { key: 'family', label: 'Family & Contact', permission: true },
    { key: 'social', label: 'Social & Education', permission: true },
    { key: 'mentorship', label: 'Mentorship', permission: true },
    { key: 'offenses', label: 'Offenses', permission: true },
    { key: 'notes', label: 'Case Notes', permission: true },
    { key: 'court', label: 'Court History', permission: true },
    { key: 'probation', label: 'Probation', permission: true },
    { key: 'communityService', label: 'Community Service', permission: true },
    { key: 'medical', label: 'Medical', permission: hasPermission('view-medical-records') },
  ].filter(tab => tab.permission);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="card space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Personal Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-slate-600">
                <p><strong>Date of Birth:</strong> {formatDate(juvenile.date_of_birth)}</p>
                <p><strong>Gender:</strong> {juvenile.gender}</p>
                <p className="md:col-span-2"><strong>Aliases:</strong> {Array.isArray(juvenile.aliases) ? juvenile.aliases.join(', ') : (juvenile.aliases || 'N/A')}</p>
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Physical Description</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-slate-600">
                <p><strong>Height:</strong> {juvenile.physical_description?.height || 'N/A'}</p>
                <p><strong>Weight:</strong> {juvenile.physical_description?.weight || 'N/A'}</p>
                <p><strong>Hair Color:</strong> {juvenile.physical_description?.hairColor || 'N/A'}</p>
                <p><strong>Eye Color:</strong> {juvenile.physical_description?.eyeColor || 'N/A'}</p>
              </div>
              <DistinctiveMarks juvenileId={juvenile.id} />
            </div>
          </div>
        );
      case 'family':
        return <FamilyMembers juvenile={juvenile} />;
      case 'social':
        return (
          <div className="card space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">School Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-slate-600">
                <p><strong>School Name:</strong> {juvenile.school_info?.name || 'N/A'}</p>
                <p><strong>Grade Level:</strong> {juvenile.school_info?.grade || 'N/A'}</p>
                <p className="md:col-span-2"><strong>Attendance:</strong> {juvenile.school_info?.attendance || 'N/A'}</p>
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Employment Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-slate-600">
                <p><strong>Employer:</strong> {juvenile.employment_info?.employer || 'N/A'}</p>
                <p><strong>Job Title:</strong> {juvenile.employment_info?.title || 'N/A'}</p>
              </div>
            </div>
          </div>
        );
      case 'mentorship':
        return <Mentorship juvenileId={juvenile.id} />;
      case 'offenses':
        return <Offenses juvenileId={juvenile.id} />;
      case 'medical':
        return <MedicalInfo juvenileId={juvenile.id} />;
      case 'notes':
        return <CaseNotes juvenileId={juvenile.id} />;
      case 'court':
        return <CourtHistory juvenileId={juvenile.id} />;
      case 'probation':
        return <ProbationDetails juvenileId={juvenile.id} />;
      case 'communityService':
        return <CommunityService juvenileId={juvenile.id} />;
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-800 mb-0">Profile: {juvenile.first_name} {juvenile.last_name}</h1>
        {hasPermission('edit-juvenile') && (
          <Link to={`/juveniles/${juvenile.id}/edit`} className="bg-gold text-white hover:bg-gold-dark font-bold py-2 px-4 rounded-lg shadow-sm">
            Edit Profile
          </Link>
        )}
      </div>

      <hr className="my-4"/>

      {/* --- NEW VERTICAL LAYOUT --- */}
      <div className="flex flex-col md:flex-row gap-8 items-start">
        {/* Vertical Tab Navigation */}
        <nav className="w-full md:w-56 flex-shrink-0" aria-label="Tabs">
          <div className="space-y-1">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={clsx(
                  'w-full text-left font-medium px-3 py-2 rounded-md text-sm transition-colors',
                  activeTab === tab.key
                    ? 'bg-gold text-white'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Tab Content Area */}
        <div className="flex-1 w-full min-w-0">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}

export default JuvenileDetailPage;